import { useMemo } from 'react';
import { useAuthStore } from '@features/authentication/store/authStore';
import { QUICK_ACTIONS, type QuickAction } from '../lib/quickActions';

/**
 * The quick actions THIS user may actually complete in THIS module (M3 · 86d44z1xm).
 *
 * A shared hook rather than a predicate re-written per call site — the same reason `useVisibleMenu`
 * exists. It subscribes to `hasPermission` ONCE and filters, because the alternative (a
 * `useAnyPermission` call per action) would be a hook inside a loop.
 *
 * ⚠️ React Compiler: `hasPermission` is subscribed as a value, so the memo's dependency is
 * something the compiler can see. Never call a store getter inside the memo body (86d3d1z40).
 */
export function useVisibleQuickActions(moduleId: string): QuickAction[] {
  const hasPermission = useAuthStore((s) => s.hasPermission);

  return useMemo(
    () =>
      QUICK_ACTIONS.filter(
        (action) =>
          action.moduleId === moduleId &&
          // The verb AND the destination's own gate — see the note in quickActions.ts. Gating on
          // the verb alone shows a shortcut the router will silently bounce to `/`.
          action.requiresAll.every((p) => hasPermission(p)) &&
          (action.requiresAny === undefined || action.requiresAny.some((p) => hasPermission(p))) &&
          // Capability gate (Scan Station needs File System Access). HIDE, never disable.
          (action.isSupported?.() ?? true)
      ),
    [moduleId, hasPermission]
  );
}
